import { formatPhone, type SessionInfo } from "@rhema/client";
import {
  Avatar,
  Button,
  ButtonLink,
  CameraIcon,
  Card,
  ChevronLeftIcon,
  ChevronRightIcon,
  cn,
  DevicesIcon,
  Eyebrow,
  focusRing,
  LogOutIcon,
  PageHeader,
  SettingRow,
  SettingsGroup,
  TextField,
  UiLink,
} from "@rhema/ui";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { MessageBackupSettings, signOutRisk } from "../backup/MessageBackup";
import { PlanCard } from "../billing/PlanCard";
import { ConnectedApps } from "../links/ConnectedApps";
import { useFeedback } from "../menus/Feedback";
import { NotificationSettings } from "../notifications/NotificationSettings";
import { MyQrCode } from "../people/MyQrCode";
import { PhoneAndPin } from "../people/PhoneAndPin";
import { PrivacySettings } from "../people/PrivacySettings";
import { photoForUpload } from "../people/photo";
import { QuietHoursSettings, StatusRow } from "../people/StatusSettings";
import { deviceName } from "../session/device";
import { usePhoneEnabled } from "../session/features";
import { useApi, useClient, useMe, useSession } from "../session/session";

function sessionLabel(s: SessionInfo): string {
  if (s.kind === "web") return deviceName(s.user_agent);
  // The desktop and mobile apps sign in with their own HTTP stack, so only the system is worth naming.
  const os = deviceName(s.user_agent).split(" on ")[1];
  return os ? `Rhema app on ${os}` : "Rhema app";
}

function lastUsed(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/** Your photo, which opens the photo picker. A camera badge says it can be changed. */
function ProfilePhoto() {
  const api = useApi();
  const me = useMe();
  const { setMe } = useSession();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setMe(await api.setAvatar(await photoForUpload(file)));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <span className="flex flex-none flex-col items-center gap-1">
      <button
        type="button"
        aria-label={me.avatar_url ? "Change profile photo" : "Add a profile photo"}
        disabled={busy}
        onClick={() => input.current?.click()}
        className={cn(
          "relative cursor-pointer rounded-full border-0 bg-transparent p-0 disabled:opacity-60",
          focusRing,
        )}
      >
        <Avatar name={me.display_name} src={me.avatar_url} tone="self" size={64} />
        <span className="absolute -right-0.5 -bottom-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-blue text-white ring-2 ring-surface-header">
          <CameraIcon size={14} strokeWidth={2.2} />
        </span>
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        onChange={(e) => void choose(e.target.files?.[0])}
      />
      {busy && <span className="text-[12px] text-ink-meta">Saving</span>}
      {error && (
        <span role="alert" className="max-w-[120px] text-center text-[12px] font-semibold text-scarlet">
          {error}
        </span>
      )}
    </span>
  );
}

function ProfileForm({ onDone }: { onDone: () => void }) {
  const api = useApi();
  const me = useMe();
  const { setMe } = useSession();
  const [name, setName] = useState(me.display_name);
  const [about, setAbout] = useState(me.about);
  const [handle, setHandle] = useState(me.handle);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user = await api.updateMe({
        display_name: name.trim(),
        about: about.trim(),
        ...(handle !== me.handle ? { handle: handle.trim().toLowerCase() } : {}),
      });
      setMe(user);
      onDone();
    } catch (err) {
      const status = (err as { status?: number }).status;
      setError(
        status === 422
          ? "Use 3 to 32 lowercase letters, numbers or underscores for your username."
          : (err as Error).message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="flex flex-col gap-3 rounded-sheet bg-surface-header p-4">
      <TextField
        label="Display name"
        required
        maxLength={80}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextField
        label="Username"
        required
        pattern="[a-z0-9_]{3,32}"
        hint="Lowercase letters, numbers and underscores. It shows on your profile."
        value={handle}
        onChange={(e) => setHandle(e.target.value)}
      />
      <TextField label="About" maxLength={140} value={about} onChange={(e) => setAbout(e.target.value)} />
      {me.avatar_url && (
        <Button
          variant="ghost"
          size="sm"
          className="self-start text-scarlet"
          disabled={busy}
          onClick={() =>
            void api
              .removeAvatar()
              .then(setMe)
              .catch((err: Error) => setError(err.message))
          }
        >
          Remove photo
        </Button>
      )}
      {error && (
        <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy}>
          {busy ? "Saving" : "Save"}
        </Button>
        <Button variant="ghost" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Devices() {
  const api = useApi();
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .sessions()
      .then(setSessions)
      .catch((e: Error) => setError(e.message));
  }, [api]);
  useEffect(load, [load]);

  const revoke = (id: string) =>
    api
      .revokeSession(id)
      .then(load)
      .catch((e: Error) => setError(e.message));

  return (
    <Card className="px-4 py-3.5">
      <div className="flex items-center gap-2">
        <DevicesIcon size={18} className="text-ink-secondary" />
        <h2 className="m-0 text-[15px] font-bold">Signed-in devices</h2>
      </div>
      {error && <p className="m-0 mt-2 text-sm text-scarlet">{error}</p>}
      {!sessions && !error && <p className="m-0 mt-2 text-sm text-ink-meta">Loading your devices.</p>}
      <ul aria-label="Signed-in devices" className="m-0 mt-1.5 list-none p-0">
        {sessions?.map((s) => (
          <li key={s.id} className="flex items-center gap-3 border-t border-line py-2.5 first:border-t-0">
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-[15px] font-semibold">
                {sessionLabel(s)}
                {s.current && (
                  <span className="ml-2 text-[13px] font-semibold text-olive-ink">This device</span>
                )}
              </span>
              <span className="text-[12.5px] text-ink-secondary">Last active {lastUsed(s.last_used_at)}</span>
            </span>
            {!s.current && (
              <Button variant="danger" size="sm" onClick={() => void revoke(s.id)}>
                Sign out
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function KeyFingerprint() {
  const client = useClient();
  const key = client.crypto?.ed25519 ?? "";
  if (!key) return null;
  return (
    <Card className="px-4 py-3.5">
      <Eyebrow>This device's identity key</Eyebrow>
      <p className="m-0 mt-1.5 font-sans text-[13px] break-all text-ink-secondary">{key}</p>
    </Card>
  );
}

/**
 * Deletes the Rhema account from the app itself, as the App Store and Google Play require. The
 * person types DELETE first, because it cannot be undone.
 */
function DeleteAccount() {
  const api = useApi();
  const { signOut } = useSession();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async (e: FormEvent) => {
    e.preventDefault();
    if (typed.trim() !== "DELETE") return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
      return;
    }
    // The server has already signed every device out. This clears the keys on this one.
    await signOut();
  };

  return (
    <section aria-labelledby="delete-account-title">
      <Card className="px-4 py-3.5">
        <h2 id="delete-account-title" className="m-0 text-[15px] font-bold">
          Delete your account
        </h2>
        <p className="m-0 mt-1.5 text-[13px] leading-normal text-ink-secondary">
          Deleting your account removes your profile from Rhema and signs out every device. If you pay for a
          plan, it is cancelled.
        </p>
        <p className="m-0 mt-1.5 text-[13px] leading-normal text-ink-secondary">
          Messages you already sent stay encrypted for the people who received them, shown as from a deleted
          account.
        </p>
        {open ? (
          <form onSubmit={remove} className="mt-3 flex flex-col gap-3">
            <TextField
              label="Type DELETE to confirm"
              autoComplete="off"
              autoCapitalize="characters"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
            {error && (
              <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
                {error}
              </p>
            )}
            <div className="flex gap-2">
              <Button type="submit" variant="danger" disabled={busy || typed.trim() !== "DELETE"}>
                {busy ? "Deleting" : "Delete account"}
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                  setTyped("");
                  setError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <Button variant="danger" size="sm" className="mt-3" onClick={() => setOpen(true)}>
            Delete account
          </Button>
        )}
      </Card>
    </section>
  );
}

// Your profile, devices and sign out.
export function YouScreen({ variant }: { variant: "phone" | "desktop" }) {
  const me = useMe();
  const { signOut, checkSignOut, platform } = useSession();
  const feedback = useFeedback();
  const phone = usePhoneEnabled();
  const [editing, setEditing] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const where = platform.native ? "device" : "browser";

  // Every message key on this device goes to the backup first. When signing out would still
  // leave messages that nothing else can read, the person decides.
  const signOutHere = async () => {
    setSigningOut(true);
    const check = await checkSignOut().catch(() => ({ safe: false, reason: "not_backed_up" }) as const);
    if (check.safe) return signOut();
    const choice = await feedback.choose(signOutRisk(check.reason, where));
    if (!choice) return setSigningOut(false);
    await signOut({ keep: choice === "other" });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen">
      <div className={variant === "desktop" ? "mx-auto w-full max-w-[640px] pb-10" : "pb-24"}>
        <PageHeader title="Settings" size={variant === "desktop" ? "md" : "lg"} />
        <div className="flex flex-col gap-3 px-4">
          {editing ? (
            <ProfileForm onDone={() => setEditing(false)} />
          ) : (
            <div className="flex items-center gap-4 rounded-sheet bg-surface-header p-4">
              <ProfilePhoto />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-serif text-2xl font-medium">{me.display_name}</span>
                <span className="select-text truncate text-sm text-ink-secondary">
                  {me.phone ? formatPhone(me.phone) : `@${me.handle}`}
                </span>
                {me.about && <span className="mt-0.5 text-sm text-ink">{me.about}</span>}
              </span>
              <span className="flex flex-col gap-1.5">
                <Button variant="ghost" className="bg-linen" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                {phone && <MyQrCode />}
              </span>
            </div>
          )}

          <StatusRow />

          {phone && <PhoneAndPin />}
          {(!phone && me.phone) || me.email ? (
            <SettingsGroup>
              {!phone && me.phone && <SettingRow title="Phone number" detail={formatPhone(me.phone)} />}
              {me.email && <SettingRow title="Email" detail={me.email} />}
            </SettingsGroup>
          ) : null}

          <PrivacySettings />

          <PlanCard />

          <NotificationSettings />

          <QuietHoursSettings />

          <SettingsGroup>
            <UiLink
              href="/later"
              className={cn("block rounded-card no-underline text-ink hover:bg-linen", focusRing)}
            >
              <SettingRow
                title="Later"
                detail="Reminders you set on messages, and messages you scheduled"
                trailing={<ChevronRightIcon size={18} className="text-ink-meta" />}
              />
            </UiLink>
          </SettingsGroup>

          <section aria-labelledby="chats-settings-title" className="flex flex-col gap-2">
            <h2 id="chats-settings-title" className="m-0 mt-1 px-1 text-[15px] font-bold">
              Chats
            </h2>
            <MessageBackupSettings />
          </section>

          <ConnectedApps />

          <SettingsGroup>
            <SettingRow
              title="Import from WhatsApp"
              detail="Bring your WhatsApp chats into Rhema, one export per chat."
              trailing={
                <ButtonLink href="/import" variant="outline" size="sm">
                  Import
                </ButtonLink>
              }
            />
          </SettingsGroup>

          <SettingsGroup>
            <UiLink
              href="/you/account"
              className={cn("block rounded-card no-underline text-ink hover:bg-linen", focusRing)}
            >
              <SettingRow
                title="Account"
                detail="Signed-in devices, this device's key and deleting your account"
                trailing={<ChevronRightIcon size={18} className="text-ink-meta" />}
              />
            </UiLink>
          </SettingsGroup>

          <Button
            variant="danger"
            className="h-12 rounded-tile text-[15px]"
            icon={<LogOutIcon size={18} />}
            disabled={signingOut}
            onClick={() => void signOutHere()}
          >
            {signingOut ? "Signing out" : "Sign out"}
          </Button>
          <p className="m-0 text-center text-[13px] text-ink-secondary">
            Signing out saves this {where}'s message keys to your encrypted backup, then removes them from
            this {where}.
          </p>
          <ButtonLink href="/status" variant="ghost" size="sm" className="self-center text-ink-secondary">
            Server status
          </ButtonLink>
        </div>
      </div>
    </div>
  );
}

/**
 * Signed-in devices, this device's key and deleting the account, one step away from Settings so
 * that deleting is never a slip of the thumb.
 */
export function AccountScreen({ variant }: { variant: "phone" | "desktop" }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen">
      <div className={variant === "desktop" ? "mx-auto w-full max-w-[640px] pb-10" : "pb-24"}>
        <div className="px-4 pt-4">
          <UiLink
            href="/you"
            className={cn(
              "inline-flex items-center gap-1 rounded px-1 text-sm font-semibold text-blue no-underline",
              focusRing,
            )}
          >
            <ChevronLeftIcon size={16} strokeWidth={2.2} />
            Settings
          </UiLink>
        </div>
        <PageHeader title="Account" size={variant === "desktop" ? "md" : "lg"} className="pt-2" />
        <div className="flex flex-col gap-3 px-4">
          <Devices />
          <KeyFingerprint />
          <DeleteAccount />
        </div>
      </div>
    </div>
  );
}
